import * as d3 from 'd3';
import { geoNaturalEarth1, geoPath, geoCentroid, geoInterpolate } from 'd3-geo';
import { feature } from 'topojson-client';

// Trade data uses UN/COMTRADE-style country names (e.g. "Korea, Republic of",
// "Taiwan (Province of China)", "Russian Federation"), while the Natural
// Earth topojson this map is built from uses shorter common names (e.g.
// "South Korea", "Taiwan", "Russia"). Everything on the left resolves to the
// map feature named on the right; anything not listed here is matched by
// exact name first, then a normalized (case/punctuation-insensitive) name.
const COUNTRY_NAME_ALIASES = {
  'united states': 'United States of America',
  'usa': 'United States of America',
  'us': 'United States of America',
  'korea, republic of': 'South Korea',
  'republic of korea': 'South Korea',
  'korea, dem. people\'s rep. of': 'North Korea',
  'dem. people\'s republic of korea': 'North Korea',
  'taiwan (province of china)': 'Taiwan',
  'chinese taipei': 'Taiwan',
  'russian federation': 'Russia',
  'congo, dem. rep.': 'Dem. Rep. Congo',
  'democratic republic of the congo': 'Dem. Rep. Congo',
  'dr congo': 'Dem. Rep. Congo',
  'congo, republic of': 'Congo',
  'republic of the congo': 'Congo',
  'ivory coast': 'Côte d\'Ivoire',
  'cote d\'ivoire': 'Côte d\'Ivoire',
  'czech republic': 'Czechia',
  'north macedonia': 'Macedonia',
  'former yugoslav republic of macedonia': 'Macedonia',
  'myanmar (burma)': 'Myanmar',
  'burma': 'Myanmar',
  'lao people\'s dem. rep.': 'Laos',
  'lao pdr': 'Laos',
  'brunei darussalam': 'Brunei',
  'bosnia herzegovina': 'Bosnia and Herz.',
  'bosnia and herzegovina': 'Bosnia and Herz.',
  'central african republic': 'Central African Rep.',
  'dominican republic': 'Dominican Rep.',
  'equatorial guinea': 'Eq. Guinea',
  'swaziland': 'eSwatini',
  'eswatini': 'eSwatini',
  'cape verde': 'Cabo Verde',
  'micronesia (federated states of)': 'Micronesia',
  'federated states of micronesia': 'Micronesia',
  'saint kitts and nevis': 'St. Kitts and Nevis',
  'saint vincent and the grenadines': 'St. Vin. and Gren.',
  'saint lucia': 'Saint Lucia',
  'st lucia': 'Saint Lucia',
  'viet nam': 'Vietnam',
  'iran (islamic republic of)': 'Iran',
  'islamic republic of iran': 'Iran',
  'syrian arab republic': 'Syria',
  'venezuela (bolivarian republic of)': 'Venezuela',
  'bolivia (plurinational state of)': 'Bolivia',
  'moldova, republic of': 'Moldova',
  'republic of moldova': 'Moldova',
  'united republic of tanzania': 'Tanzania',
  'united kingdom of great britain and northern ireland': 'United Kingdom',
  'china, hong kong sar': 'Hong Kong',
  'china, macao sar': 'Macao',
  'antigua and barbuda': 'Antigua and Barb.',
  'solomon islands': 'Solomon Is.',
  'marshall islands': 'Marshall Is.',
  'trinidad & tobago': 'Trinidad and Tobago',
};

const normalize = (name) => (name || '').toLowerCase().trim().replace(/[.,'’()&]/g, '').replace(/\s+/g, ' ');

// The alias table above is written with natural punctuation for readability,
// but normalize() strips punctuation from whatever name it's matching against
// - so the keys have to go through the same normalization once here, or an
// alias like 'Taiwan (Province of China)' would never match its normalized
// lookup key 'taiwan province of china'.
const NORMALIZED_ALIASES = Object.fromEntries(
  Object.entries(COUNTRY_NAME_ALIASES).map(([k, v]) => [normalize(k), v])
);

let worldPromise = null;

// Loads once and caches: the drawable land/country polygons, plus a lookup
// from normalized name -> feature, used to resolve trade partner names to
// map geometry (and hence a centroid to anchor a flow arc at).
export function loadWorldMap() {
  if (!worldPromise) {
    worldPromise = import('world-atlas/countries-50m.json').then((mod) => {
      const topology = mod.default || mod;
      const countries = feature(topology, topology.objects.countries).features;
      const byNormalizedName = new Map();
      countries.forEach((f) => {
        byNormalizedName.set(normalize(f.properties?.name), f);
      });
      return { countries, byNormalizedName };
    });
  }
  return worldPromise;
}

// Resolves a trade-data country name to its map feature, trying an exact
// (normalized) match first, then the alias table above. Returns null if the
// name can't be matched - callers should skip that partner rather than throw,
// since this is a best-effort visualization, not the source of truth.
export function resolveCountryFeature(name, byNormalizedName) {
  const n = normalize(name);
  if (byNormalizedName.has(n)) return byNormalizedName.get(n);
  const alias = NORMALIZED_ALIASES[n];
  if (alias) return byNormalizedName.get(normalize(alias));
  return null;
}

/**
 * Re-thickens the arcs drawFlowMap drew into svgEl by `scale` (1 = default
 * widths), without redrawing the map - so the zoom/pan is kept.
 */
export function setFlowMapWidthScale(svgEl, scale) {
  if (!svgEl) return;
  svgEl.__arcWidthScale = scale;
  d3.select(svgEl).selectAll('.flow-map-arc').attr('stroke-width', d => d.baseWidth * scale);
}

/**
 * Renders a world map with flow arcs between countries into svgEl.
 *
 * @param {SVGElement} svgEl
 * @param {{countries: object[], byNormalizedName: Map}} world - from loadWorldMap()
 * @param {Array<{from: string, to: string, flow: number, units: string, raw?: object}>} links
 * @param {object} opts
 * @param {string|null} opts.centerName - if set, every arc is drawn between this
 *   country and the OTHER name on the link (hub mode, e.g. CDTF). If null, arcs
 *   are drawn directly between each link's own from/to (all-to-all mode, e.g. TF).
 * @param {(name: string) => string} opts.colorOf
 * @param {(name: string, link: object) => string} [opts.nodeColorOf] - country dot colour; defaults to colorOf
 * @param {(name: string) => boolean} [opts.isDimmed]
 * @param {(event: MouseEvent, link: object) => void} opts.onHover
 * @param {(event: MouseEvent) => void} opts.onMove
 * @param {() => void} opts.onLeave
 * @param {(event: MouseEvent, summary: PinSummary) => void} [opts.onPinHover] - hovering a
 *   country pin; gets that country's trades among the links drawn (so it
 *   follows whatever filters produced `links`). onMove/onLeave are reused.
 * @param {number} [opts.widthScale=1] - multiplies every arc's thickness; can
 *   be changed later without a redraw via setFlowMapWidthScale
 *
 * @typedef {{ name: string, units: string,
 *   exports: {partner: string, flow: number}[], imports: {partner: string, flow: number}[],
 *   exportTotal: number, importTotal: number }} PinSummary
 */
export function drawFlowMap(svgEl, world, links, opts) {
  const { centerName = null, colorOf, nodeColorOf = colorOf, isDimmed = () => false, onHover, onMove, onLeave, onPinHover, widthScale = 1 } = opts;
  svgEl.__arcWidthScale = widthScale;
  const W = svgEl.clientWidth || 900;
  const H = svgEl.clientHeight || 520;

  d3.select(svgEl).selectAll('*').remove();
  const root = d3.select(svgEl);
  const defs = root.append('defs');
  // Everything drawable goes into this <g> rather than straight onto the SVG,
  // so a single zoom transform on the group scrolls/scales the whole map -
  // scroll or pinch to zoom, drag to pan, same interaction as any web map.
  const svg = root.append('g');

  const projection = geoNaturalEarth1().fitSize([W, H], { type: 'Sphere' });
  const pathGen = geoPath(projection);

  // Base landmass
  svg.append('path')
    .attr('d', pathGen({ type: 'Sphere' }))
    .attr('fill', '#f4f6fb').attr('stroke', 'none');
  svg.append('g').selectAll('path')
    .data(world.countries)
    .join('path')
    .attr('d', pathGen)
    .attr('fill', '#e0e3ef')
    .attr('stroke', '#ffffff')
    .attr('stroke-width', 0.5)
    .attr('vector-effect', 'non-scaling-stroke');

  const centroidCache = new Map();
  function centroidFor(name) {
    if (centroidCache.has(name)) return centroidCache.get(name);
    const f = resolveCountryFeature(name, world.byNormalizedName);
    const c = f ? geoCentroid(f) : null;
    centroidCache.set(name, c);
    return c;
  }

  const arcsLayer = svg.append('g');
  const nodesLayer = svg.append('g');
  const pins = new Map(); // name → { x, y, link } - drawn after the arcs, on top
  let unresolvedCount = 0;

  links.forEach((link, i) => {
    // Hub mode: `a` is always the fixed centre; `b` is whichever side of the
    // link isn't the centre (the partner). All-to-all mode: no fixed centre,
    // so `a`/`b` are just the link's own from/to, and colour keys off the
    // origin side since there's no "partner relative to a hub" concept.
    const a = centerName || link.from;
    const b = centerName ? (link.from === centerName ? link.to : link.from) : link.to;

    const pa = centroidFor(a);
    const pb = centroidFor(b);
    if (!pa || !pb) { unresolvedCount++; return; }

    // Hub mode dims by the partner alone. All-to-all mode: an arc stays bright
    // if EITHER endpoint is spotlighted (not both) - isDimmed(x) already means
    // "x itself isn't in the spotlight", so dim only when neither end is.
    const dimmed = centerName ? isDimmed(b) : (isDimmed(a) && isDimmed(b));
    // colorOf gets the link too (not just the partner name) so callers can
    // colour by import/export side instead of by partner identity when that
    // makes more sense (e.g. CDTF's map: all imports one colour, all exports
    // another). All-to-all callers that don't care can just ignore it.
    const col = colorOf(centerName ? b : a, link);

    const [x0, y0] = projection(pa);
    const [x1, y1] = projection(pb);

    // Great-circle interpolated arc, bowed slightly toward the pole side so
    // overlapping routes fan out instead of stacking as straight overlapping
    // lines - the same reason flight-path maps curve their routes.
    const interpolate = geoInterpolate(pa, pb);
    const mid = interpolate(0.5);
    const [mx, my] = projection(mid);
    const dx = x1 - x0, dy = y1 - y0;
    const dist = Math.hypot(dx, dy) || 1;
    const bow = Math.min(dist * 0.18, 60);
    const nx = -dy / dist, ny = dx / dist;
    const cx = mx + nx * bow, cy = my + ny * bow;

    const gradId = `map-arc-${i}`;
    const grad = defs.append('linearGradient').attr('id', gradId).attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', x0).attr('y1', y0).attr('x2', x1).attr('y2', y1);
    grad.append('stop').attr('offset', '0%').attr('stop-color', col).attr('stop-opacity', dimmed ? 0.1 : 0.75);
    grad.append('stop').attr('offset', '100%').attr('stop-color', col).attr('stop-opacity', dimmed ? 0.05 : 0.35);

    const flow = Number(link.flow) || 0;
    const baseWidth = Math.max(1, Math.min(6, Math.sqrt(flow) * 0.06));
    // Read the scale at hover time, so a later setFlowMapWidthScale sticks
    const width = () => baseWidth * svgEl.__arcWidthScale;

    arcsLayer.append('path')
      .datum({ baseWidth })
      .attr('class', 'flow-map-arc')
      .attr('d', `M${x0},${y0} Q${cx},${cy} ${x1},${y1}`)
      .attr('fill', 'none')
      .attr('stroke', `url(#${gradId})`)
      .attr('stroke-width', width())
      .attr('stroke-linecap', 'round')
      .attr('vector-effect', 'non-scaling-stroke')
      .attr('stroke-opacity', dimmed ? 0.4 : 1)
      .on('mouseenter', function (ev) { if (!dimmed) d3.select(this).attr('stroke-width', width() + 1.5); onHover?.(ev, link); })
      .on('mousemove', (ev) => onMove?.(ev))
      .on('mouseleave', function () { d3.select(this).attr('stroke-width', width()); onLeave?.(); });

    [[a, x0, y0], [b, x1, y1]].forEach(([name, x, y]) => {
      if (!pins.has(name)) pins.set(name, { x, y, link });
    });
  });

  // Pins, each with a hover summary of that country's trades among the drawn
  // links (link.from exports to link.to).
  pins.forEach(({ x, y, link }, name) => {
    const isCenter = name === centerName;
    const pin = nodesLayer.append('g');
    pin.append('circle')
      .attr('cx', x).attr('cy', y)
      .attr('r', isCenter ? 5 : 3)
      .attr('fill', isCenter ? '#213CE5' : nodeColorOf(name, link))
      .attr('fill-opacity', !isCenter && isDimmed(name) ? 0.3 : 1)
      .attr('stroke', '#ffffff').attr('stroke-width', 1)
      .attr('vector-effect', 'non-scaling-stroke');
    if (!onPinHover) return;
    // Pins are tiny - a transparent, larger circle gives them a usable target
    pin.append('circle')
      .attr('cx', x).attr('cy', y).attr('r', 8)
      .attr('fill', 'transparent')
      .style('cursor', 'default')
      .on('mouseenter', (ev) => onPinHover(ev, pinSummary(name, links)))
      .on('mousemove', (ev) => onMove?.(ev))
      .on('mouseleave', () => onLeave?.());
  });

  const zoom = d3.zoom()
    .scaleExtent([1, 10])
    .translateExtent([[0, 0], [W, H]])
    .on('zoom', (event) => svg.attr('transform', event.transform));
  root.call(zoom);

  return { unresolvedCount };
}

// One country's exports (it's link.from) and imports (it's link.to) among
// `links`, partners merged and sorted biggest first.
function pinSummary(name, links) {
  const exports = new Map(), imports = new Map();
  let units = '';
  for (const l of links) {
    const flow = Number(l.flow) || 0;
    if (!flow) continue;
    if (l.from === name) exports.set(l.to, (exports.get(l.to) || 0) + flow);
    else if (l.to === name) imports.set(l.from, (imports.get(l.from) || 0) + flow);
    else continue;
    units ||= l.units || '';
  }
  const sorted = m => [...m].map(([partner, flow]) => ({ partner, flow })).sort((a, b) => b.flow - a.flow);
  const total = m => [...m.values()].reduce((s, v) => s + v, 0);
  return { name, units, exports: sorted(exports), imports: sorted(imports), exportTotal: total(exports), importTotal: total(imports) };
}

/**
 * Plain-text lines for a pin tooltip: an Exports and an Imports block, each a
 * total followed by its biggest partners (the rest rolled into "+N more").
 * @param {PinSummary} s
 * @returns {{ title: string, lines: {text: string, sub?: boolean}[] }}
 */
export function pinTooltipLines(s, maxPartners = 5) {
  const fmt = v => `${v.toLocaleString('en', { maximumFractionDigits: 1 })}${s.units ? ' ' + s.units : ''}`;
  const lines = [];
  const block = (label, arrow, rows, total) => {
    if (!rows.length) return;
    lines.push({ text: `${label}: ${fmt(total)}` });
    rows.slice(0, maxPartners).forEach(r => lines.push({ text: `${arrow} ${r.partner}: ${fmt(r.flow)}`, sub: true }));
    if (rows.length > maxPartners) lines.push({ text: `+${rows.length - maxPartners} more`, sub: true });
  };
  block('Exports', '→', s.exports, s.exportTotal);
  block('Imports', '←', s.imports, s.importTotal);
  return { title: s.name, lines };
}
